import awsdk from 'awsdk';
import {
  Consumer,
  Condition,
  Allergy,
  Vitals,
  Medication,
  Subscription,
  PaymentMethod,
  Authentication,
  MedicationList,
  ConsumerUpdate,
  PaymentRequest,
  SubscriptionUpdate,
  DependentRegistration,
  Visit,
  HealthDocumentRecord
} from 'lib/amwell/sdk/types';
import ValidationError from '../errors/ValidationError';
import BaseService from './BaseService';
import AwsdkError from '../errors/AwsdkError';

export default class ConsumerService extends BaseService {
  async getConsumer(authentication: Authentication): Promise<Consumer> {
    const key = this.getConsumer.name;
    try {
      return await this.sdk.consumerService.getConsumer(authentication);
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async getUpdatedConsumer(consumer: Consumer): Promise<Consumer> {
    const key = this.getUpdatedConsumer.name;

    try {
      return await this.sdk.consumerService.getUpdatedConsumer(consumer);
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async getDependents(consumer: Consumer): Promise<Consumer[]> {
    const key = this.getDependents.name;

    try {
      return await this.sdk.consumerService.getDependents(consumer);
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  newDependentRegistration(): DependentRegistration {
    return this.sdk.consumerService.newDependentRegistration();
  }

  async registerDependent(
    consumer: Consumer,
    dependentRegistration: DependentRegistration
  ): Promise<boolean> {
    const key = this.registerDependent.name;

    try {
      const errors = this.sdk.consumerService.validateDependentRegistration(dependentRegistration);

      if (errors && errors.length) {
        const validationError = new awsdk.AWSDKError(awsdk.AWSDKErrorCode.validationErrors);
        validationError.errors = errors;
        throw validationError;
      }

      await this.sdk.consumerService.registerDependent(consumer, dependentRegistration);
      return true;
    } catch (err) {
      if (
        err instanceof awsdk.AWSDKError &&
        err.errorCode === awsdk.AWSDKErrorCode.validationErrors
      ) {
        throw new ValidationError(key, err.errors || err);
      }
      throw new AwsdkError(key, err);
    }
  }

  async getMedications(consumer: Consumer): Promise<MedicationList> {
    const key = this.getMedications.name;

    try {
      return await this.sdk.consumerService.getMedications(consumer);
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async addServiceKey(consumer: Consumer, serviceKey: string): Promise<Consumer> {
    const key = this.addServiceKey.name;

    try {
      return await this.sdk.consumerService.addServiceKey(consumer, serviceKey);
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async updateMedications(consumer: Consumer, medications: Medication[]): Promise<Medication[]> {
    const key = this.updateMedications.name;

    try {
      await this.sdk.consumerService.updateMedications(consumer, medications);
      return medications;
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async searchMedications(consumer: Consumer, searchText: string): Promise<MedicationList> {
    const key = this.searchMedications.name;

    try {
      return await this.sdk.consumerService.searchMedications(consumer, searchText);
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async getConditions(consumer: Consumer): Promise<Condition[]> {
    const key = this.getConditions.name;

    try {
      return await this.sdk.consumerService.getConditions(consumer);
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async updateConditions(consumer: Consumer, conditions: Condition[]): Promise<Condition[]> {
    const key = this.updateConditions.name;

    try {
      await this.sdk.consumerService.updateConditions(consumer, conditions);
      return conditions;
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async getAllergies(consumer: Consumer): Promise<Allergy[]> {
    const key = this.getAllergies.name;

    try {
      return await this.sdk.consumerService.getAllergies(consumer);
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async updateAllergies(consumer: Consumer, allergies: Allergy[]): Promise<Allergy[]> {
    const key = this.updateAllergies.name;

    try {
      await this.sdk.consumerService.updateAllergies(consumer, allergies);
      return allergies;
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async getVitals(consumer: Consumer): Promise<Vitals> {
    const key = this.getVitals.name;

    try {
      return await this.sdk.consumerService.getVitals(consumer);
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async updateVitals(consumer: Consumer, vitals: Vitals): Promise<Vitals> {
    const key = this.updateVitals.name;

    try {
      await this.sdk.consumerService.updateVitals(consumer, vitals);
      return vitals;
    } catch (err) {
      if (err instanceof awsdk.AWSDKError) {
        if (
          err instanceof awsdk.AWSDKError &&
          err.errorCode === awsdk.AWSDKErrorCode.validationErrors
        ) {
          throw new ValidationError(key, err.errors);
        }
      }
      throw new AwsdkError(key, err);
    }
  }

  async getInsuranceSubscription(consumer: Consumer): Promise<Subscription> {
    const key = this.getInsuranceSubscription.name;

    try {
      return await this.sdk.consumerService.getInsuranceSubscription(consumer);
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  newSubscriptionUpdate(): SubscriptionUpdate {
    return this.sdk.consumerService.newSubscriptionUpdate();
  }

  async updateInsuranceSubscription(
    consumer: Consumer,
    subscriptionUpdate: SubscriptionUpdate
  ): Promise<boolean> {
    const key = this.updateInsuranceSubscription.name;

    try {
      const errors = this.sdk.consumerService.validateSubscriptionUpdate(
        subscriptionUpdate,
        consumer
      );

      if (errors && errors.length) {
        const validationError = new awsdk.AWSDKError(awsdk.AWSDKErrorCode.validationErrors);
        validationError.errors = errors;
        throw validationError;
      }

      await this.sdk.consumerService.updateInsuranceSubscription(consumer, subscriptionUpdate);
      return true;
    } catch (err) {
      if (
        err instanceof awsdk.AWSDKError &&
        err.errorCode === awsdk.AWSDKErrorCode.validationErrors
      ) {
        throw new ValidationError(key, err.errors || err);
      }
      throw new AwsdkError(key, err);
    }
  }

  async removeInsuranceSubscription(consumer: Consumer): Promise<boolean> {
    const key = this.removeInsuranceSubscription.name;

    try {
      await this.sdk.consumerService.removeInsuranceSubscription(consumer);
      return true;
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async getPaymentMethod(consumer: Consumer): Promise<PaymentMethod | null> {
    const key = this.getPaymentMethod.name;

    try {
      return await this.sdk.consumerService.getPaymentMethod(consumer);
    } catch (err) {
      if (err instanceof awsdk.AWSDKError) {
        if (err.errorCode === awsdk.AWSDKErrorCode.noPaymentInformationFound) {
          return null;
        }
      }
      throw new AwsdkError(key, err);
    }
  }

  newPaymentRequest(): PaymentRequest {
    return this.sdk.consumerService.newPaymentRequest();
  }

  async updatePaymentMethod(
    consumer: Consumer,
    paymentRequest: PaymentRequest
  ): Promise<PaymentMethod> {
    const key = this.updatePaymentMethod.name;

    try {
      return await this.sdk.consumerService.updatePaymentMethod(consumer, paymentRequest);
    } catch (err) {
      if (
        err instanceof awsdk.AWSDKError &&
        err.errorCode === awsdk.AWSDKErrorCode.validationErrors
      ) {
        throw new ValidationError(key, err.errors);
      }

      throw new AwsdkError(key, err);
    }
  }

  newConsumerUpdate(): ConsumerUpdate {
    return this.sdk.consumerService.newConsumerUpdate();
  }

  async updateConsumer(consumer: Consumer, consumerUpdate: ConsumerUpdate): Promise<Consumer> {
    const key = this.updateConsumer.name;

    try {
      const errors = this.sdk.consumerService.validateConsumerUpdate(consumerUpdate);

      if (errors && errors.length) {
        const validationError = new awsdk.AWSDKError(awsdk.AWSDKErrorCode.validationErrors);
        validationError.errors = errors;
        throw validationError;
      }

      return await this.sdk.consumerService.updateConsumer(consumer, consumerUpdate);
    } catch (err) {
      if (
        err instanceof awsdk.AWSDKError &&
        err.errorCode === awsdk.AWSDKErrorCode.validationErrors
      ) {
        throw new ValidationError(key, err.errors || err);
      }

      throw new AwsdkError(key, err);
    }
  }

  async searchHealthDocumentRecords(
    consumer: Consumer,
    options?: { sortAsc: true }
  ): Promise<HealthDocumentRecord[]> {
    const key = this.searchHealthDocumentRecords.name;

    try {
      const list = await this.sdk.consumerService.searchHealthDocumentRecords(consumer, options);
      return list.healthDocumentRecords;
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async addHealthDocumentAttachment(
    consumer: Consumer,
    file: Blob | File,
    fileName: string,
    visit?: Visit,
    comment?: string
  ): Promise<HealthDocumentRecord> {
    const key = this.addHealthDocumentAttachment.name;

    try {
      return await this.sdk.consumerService.addHealthDocumentAttachment(
        consumer,
        file,
        fileName,
        visit,
        comment
      );
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }

  async removeHealthDocumentRecord(
    consumer: Consumer,
    healthDocumentRecord: HealthDocumentRecord
  ): Promise<boolean> {
    const key = this.removeHealthDocumentRecord.name;

    try {
      return await this.sdk.consumerService.removeHealthDocumentRecord(
        consumer,
        healthDocumentRecord
      );
    } catch (err) {
      throw new AwsdkError(key, err);
    }
  }
}
